import React, { useMemo } from 'react';
import ButtonContainer from '../components/ButtonContainer';
import { FormattedMessage } from 'react-intl';
import errorImage from '../images/Error.svg';
import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import styled from '@emotion/styled';
import { VerificationType } from '../types';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';

const MessageContainer = styled.div`
  margin-bottom: 24px;
`;

export type VerifyOrReverifyEmailRateLimitExceededErrorPageProps = {
  recoverUrl: string;
  verificationType: VerificationType;
};

const pageId = 'reverifyEmailRateLimitExceededErrorPage';

export const VerifyOrReverifyEmailRateLimitExceededErrorPage = ({
  recoverUrl,
  verificationType,
}: VerifyOrReverifyEmailRateLimitExceededErrorPageProps) => {
  const navigate = useNavigateSoftOrHard();
  const analyticsClient = useAnalyticsClient();
  const displayRecoverUrl = useMemo(() => new URL(recoverUrl).hostname, [recoverUrl]);

  usePageViewedEvent(pageId, { flow: verificationType });

  const recover = () => {
    analyticsClient.buttonClickedEvent(pageId, 'backToStartButton', { flow: verificationType });
    navigate(recoverUrl);
  };

  return (
    <WhiteboxPage
      pageId={pageId}
      header={
        <div>
          <MessageContainer>
            <FormattedMessage
              id="reverify.email.rate-limit-exceeded.error.title"
              defaultMessage="We couldn't verify your email"
              tagName="h5"
            />
          </MessageContainer>
          <img src={errorImage} width="99" alt="" />
        </div>
      }>
      <FormattedMessage
        id="reverify.email.rate-limit-exceeded.error.main-action"
        defaultMessage="There have been too many attempts to reverify your account in the past hour. You'll need to try again later."
        description="Telling the user that they have exceeded welcome email limit"
      />
      <ButtonContainer>
        <TallButton appearance="primary" id="go-back-button" isFullWidth onClick={recover}>
          <FormattedMessage
            id="reverify.email.rate-limit-exceeded.error.button-text"
            defaultMessage="Go back to {startUrl}"
            values={{ startUrl: displayRecoverUrl }}
          />
        </TallButton>
      </ButtonContainer>
    </WhiteboxPage>
  );
};

export default VerifyOrReverifyEmailRateLimitExceededErrorPage;
